import { useCallback, useEffect, useState } from 'react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { CollectedAt, GradeChip, Pill, label } from './components/primitives'
import { createWatch, listSubjects, listWatches, type Subject, type Watch } from './api'

const CATEGORIES = ['LEGAL_INTEGRITY', 'BUSINESS_WEALTH', 'POLITICAL_HISTORY', 'SOCIAL_INTEL',
  'MEDIA_COVERAGE', 'FAMILY_NETWORK', 'POLLING', 'OPERATIONAL']

/**
 * Standing questions.
 *
 * ## The cooldown is a first-class field, not an advanced setting
 *
 * A watch on "any new item" against a busy subject during a campaign fires forty times an
 * hour, and forty alerts an hour is zero alerts — people turn them off and then miss the one
 * that mattered. So the cooldown sits on the create form with its consequence spelled out,
 * rather than defaulting invisibly.
 *
 * ## The condition is declarative
 *
 * `{kind, params}` — a closed set of kinds, for the same reason the item taxonomy is closed.
 * An open condition language is a program stored in a database column.
 */
export function WatchesPage() {
  const [watches, setWatches] = useState<Watch[] | null>(null)
  const [subjects, setSubjects] = useState<Subject[]>([])
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const load = useCallback(async () => {
    try {
      setError(null)
      const [w, s] = await Promise.all([listWatches(), listSubjects()])
      setWatches(w)
      setSubjects(s)
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (error && !watches) return <ErrorStrip message={error} />
  if (!watches) return <LoadingBlock label="Loading watches" />

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Watches"
        description="A standing question — tell me when something of this shape lands on this
                     subject."
      />
      {error && <ErrorStrip message={error} />}

      <button type="button" onClick={() => setAdding((a) => !a)}
              className="rounded border border-border px-2 py-1 text-[12px]">
        {adding ? 'Close' : 'New watch'}
      </button>

      {adding && (
        <NewWatchForm subjects={subjects} onDone={() => { setAdding(false); void load() }} />
      )}

      <Panel>
        <ErpTable head={<>
          <Th>Watch</Th>
          <Th>Condition</Th>
          <Th className="axis-hide-sm">Severity</Th>
          <Th className="text-right">Cooldown</Th>
          <Th className="text-right axis-hide-sm">Fired</Th>
          <Th>Last fired</Th>
          <Th>State</Th>
        </>}>
          {watches.length === 0 && (
            <EmptyRow colSpan={7}
                      message="No watches. Without one the dossier is a filing cabinet rather
                               than a live product." />
          )}
          {watches.map((w) => (
            <tr key={w.id} className="hover:bg-muted/40">
              <Td>
                <span className="font-medium">{w.name}</span>
                {w.description && (
                  <span className="block text-[10px] text-muted-foreground">{w.description}</span>
                )}
              </Td>
              <Td><span className="font-mono text-[10px]">{summarise(w.conditionJson)}</span></Td>
              <Td className="axis-hide-sm">
                <Pill className="border-border">{label(w.severity)}</Pill>
              </Td>
              <Td className="text-right tabular-nums">
                {w.cooldownSeconds === 0 ? 'none' : `${Math.round(w.cooldownSeconds / 60)}m`}
              </Td>
              <Td className="text-right tabular-nums axis-hide-sm">{w.fireCount}</Td>
              <Td><CollectedAt at={w.lastFiredAt} /></Td>
              <Td>
                <Pill className={w.enabled
                  ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                  : 'border-border text-muted-foreground'}>
                  {w.enabled ? 'Watching' : 'Paused'}
                </Pill>
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>
    </div>
  )
}

function NewWatchForm({ subjects, onDone }: { subjects: Subject[]; onDone: () => void }) {
  const [name, setName] = useState('')
  const [subjectId, setSubjectId] = useState('')
  const [kind, setKind] = useState('ITEM_MATCH')
  const [categories, setCategories] = useState<string[]>([])
  const [maxCredibility, setMaxCredibility] = useState('3')
  const [severity, setSeverity] = useState('MEDIUM')
  const [cooldownMinutes, setCooldownMinutes] = useState(60)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await createWatch({
        name,
        subjectId: subjectId || null,
        condition: kind === 'ITEM_MATCH'
          ? { kind, params: { categories, maxCredibility } }
          : { kind, params: {} },
        severity,
        cooldownSeconds: cooldownMinutes * 60,
      })
      onDone()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel>
      <form onSubmit={submit} className="grid gap-2 p-3 sm:grid-cols-2">
        {error && <div className="sm:col-span-2"><ErrorStrip message={error} /></div>}

        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} required
                 placeholder="Legal integrity on the governor race"
                 className="h-7 w-full rounded border border-input bg-background px-2 text-[12px]" />
        </Field>
        <Field label="Subject (blank = every subject in scope)">
          <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)}
                  className="h-7 w-full rounded border border-input bg-background px-2 text-[12px]">
            <option value="">Any subject</option>
            {subjects.map((s) => <option key={s.id} value={s.id}>{s.displayName}</option>)}
          </select>
        </Field>
        <Field label="Fires on">
          <select value={kind} onChange={(e) => setKind(e.target.value)}
                  className="h-7 w-full rounded border border-input bg-background px-2 text-[12px]">
            <option value="NEW_ITEM">Any new item</option>
            <option value="ITEM_MATCH">An item matching a shape</option>
          </select>
        </Field>
        <Field label="Severity">
          <select value={severity} onChange={(e) => setSeverity(e.target.value)}
                  className="h-7 w-full rounded border border-input bg-background px-2 text-[12px]">
            {['LOW', 'MEDIUM', 'HIGH', 'EXTREME'].map((s) => (
              <option key={s} value={s}>{label(s)}</option>
            ))}
          </select>
        </Field>

        {kind === 'ITEM_MATCH' && (
          <>
            <Field label="Categories (none = any)">
              <div className="flex flex-wrap gap-1">
                {CATEGORIES.map((c) => (
                  <button key={c} type="button"
                          onClick={() => setCategories((cur) => cur.includes(c)
                            ? cur.filter((x) => x !== c) : [...cur, c])}
                          className={`rounded border px-1.5 py-0.5 text-[10px] ${
                            categories.includes(c)
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border text-muted-foreground'}`}>
                    {label(c)}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Only claims at least this credible">
              <div className="flex items-center gap-2">
                <select value={maxCredibility}
                        onChange={(e) => setMaxCredibility(e.target.value)}
                        className="h-7 rounded border border-input bg-background px-2
                                   text-[12px]">
                  {['1', '2', '3', '4', '5'].map((c) => (
                    <option key={c} value={c}>{c} or better</option>
                  ))}
                </select>
                <GradeChip grade={`C${maxCredibility}`} />
              </div>
            </Field>
          </>
        )}

        <Field label="Cooldown (minutes)">
          <input type="number" min={0} value={cooldownMinutes}
                 onChange={(e) => setCooldownMinutes(Number(e.target.value))}
                 className="h-7 w-full rounded border border-input bg-background px-2 text-[12px]" />
          <span className="text-[10px] text-muted-foreground">
            One alert per window. Forty alerts an hour is zero alerts — people turn them off
            and then miss the one that mattered.
          </span>
        </Field>

        <div className="sm:col-span-2">
          <button type="submit" disabled={busy}
                  className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                             text-primary-foreground disabled:opacity-50">
            {busy ? 'Creating…' : 'Create watch'}
          </button>
        </div>
      </form>
    </Panel>
  )
}

function Field({ label: text, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{text}</span>
      {children}
    </label>
  )
}

/** A one-line reading of the condition jsonb, for the list. */
function summarise(json: string): string {
  try {
    const parsed = JSON.parse(json) as { kind?: string; params?: Record<string, unknown> }
    const kind = parsed.kind ?? 'NEW_ITEM'
    const cats = (parsed.params?.categories as string[] | undefined) ?? []
    const cred = parsed.params?.maxCredibility as string | undefined
    return [kind, cats.length ? cats.join('/') : null, cred ? `≤${cred}` : null]
      .filter(Boolean).join(' · ')
  } catch {
    return json
  }
}
